import {
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  Paper,
  Skeleton,
  Table,
  Tooltip,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Add,
  ChevronLeft,
  ChevronRight,
  HowToReg,
  LocationOn,
  Close,
} from "@mui/icons-material";
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import theme from "../theme/theme";
import { useEffect, useState, useMemo, useRef } from "react";
import { fetchEmployeePage } from "../api/EmployeeService";
import AsyncAutocomplete from "../components/common/AsyncAutocomplete";
import {
  fetchEmployeeAttendance,
  fetchEmployeeLocations,
} from "../api/AttendanceService";
import { onMessage } from "firebase/messaging";
import { messaging } from "../firebase-config";
import { toast } from "react-toastify";
import { Edit } from "@mui/icons-material";
import { EditAttendanceDialog } from "../components/attendance/EditAttendanceDialog";
import { CreateAttendanceDialog } from "../components/attendance/CreateAttendanceDialog";
import { updateAttendance, createAttendance } from "../api/AttendanceService";

// Fix default marker icons for Leaflet in webpack/CRA
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom numbered marker icon with highlight and bounce support
const createNumberedIcon = (
  number,
  total,
  isHighlighted = false,
  isBouncing = false,
) => {
  const isFirst = number === 1;
  const isLast = number === total;
  const color = isFirst ? "#4CAF50" : isLast ? "#f44336" : "#1976d2";
  const scale = isHighlighted ? 1.3 : 1;
  const size = Math.round(28 * scale);
  const bounceAnimation = isBouncing
    ? "animation: markerBounce 0.5s ease infinite;"
    : "";
  const highlightBorder = isHighlighted ? "3px solid #fff" : "2px solid #fff";
  const highlightShadow = isHighlighted
    ? "0 4px 12px rgba(0,0,0,0.4)"
    : "0 2px 6px rgba(0,0,0,0.3)";
  return L.divIcon({
    className: "custom-marker",
    html: `
      <style>
        @keyframes markerBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      </style>
      <div style="
        background:${color};
        color:#fff;
        border-radius:50%;
        width:${size}px;
        height:${size}px;
        display:flex;
        align-items:center;
        justify-content:center;
        font-size:${Math.round(12 * scale)}px;
        font-weight:bold;
        border:${highlightBorder};
        box-shadow:${highlightShadow};
        ${bounceAnimation}
      ">${number}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
};

// Haversine distance calculation (returns meters)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Group consecutive locations within threshold distance
const clusterLocations = (locations, thresholdMeters = 50) => {
  if (!locations || locations.length === 0) return [];
  const sorted = [...locations]
    .filter((loc) => loc.latitude && loc.longitude)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  if (sorted.length === 0) return [];

  const clusters = [];
  let currentCluster = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = currentCluster[currentCluster.length - 1];
    const curr = sorted[i];
    const dist = calculateDistance(
      parseFloat(prev.latitude),
      parseFloat(prev.longitude),
      parseFloat(curr.latitude),
      parseFloat(curr.longitude),
    );
    if (dist <= thresholdMeters) {
      currentCluster.push(curr);
    } else {
      clusters.push(currentCluster);
      currentCluster = [curr];
    }
  }
  clusters.push(currentCluster);
  return clusters;
};

// Component to auto-fit map bounds (only runs once on initial load)
const FitBounds = ({ locations }) => {
  const map = useMap();
  const hasFitted = useRef(false);
  useEffect(() => {
    if (locations.length > 0 && !hasFitted.current) {
      hasFitted.current = true;
      // Force Leaflet to recalculate container size
      setTimeout(() => {
        map.invalidateSize();
        const bounds = L.latLngBounds(
          locations.map((loc) => [loc.latitude, loc.longitude]),
        );
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
      }, 100);
    }
  }, [locations, map]);
  return null;
};

// Component to fly to a specific location when clicked from the list
const FlyToLocation = ({ location }) => {
  const map = useMap();
  useEffect(() => {
    if (location && location.lat && location.lng) {
      map.flyTo([location.lat, location.lng], 18, { duration: 0.8 });
    }
  }, [location?.timestamp, map]);
  return null;
};
const AttendancePage = () => {
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedEmployeeOption, setSelectedEmployeeOption] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [attendanceData, setAttendanceData] = useState({});
  const [rawAttendances, setRawAttendances] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [locationData, setLocationData] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [highlightedLocationId, setHighlightedLocationId] = useState(null);
  const [hoveredClusterId, setHoveredClusterId] = useState(null);
  const [flyToLocation, setFlyToLocation] = useState(null);
  const logRefs = useRef({});
  const logPanelRef = useRef(null);
  // Date to auto-select once the next calendar fetch lands (set after creating a record)
  const pendingSelectDateRef = useRef(null);

  // Edit Dialog State
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAttendance, setEditingAttendance] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Create Dialog State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const BASE_URL = "https://api.rainbowlifts.com";

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getStatusColor = (status) => {
    switch (status) {
      case "present":
        return "green";
      case "absent":
        return "red";
      case "half_day":
        return "orange";
      case "holiday":
        return "grey";
      case "weekend":
        return "grey";
      case "leave":
        return "#2196f3";
      case "pending":
        return "#ff9800";
      default:
        return "transparent";
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case "present":
        return "rgba(0, 128, 0, 0.1)";
      case "absent":
        return "rgba(255, 0, 0, 0.1)";
      case "half_day":
        return "rgba(255, 165, 0, 0.1)";
      case "holiday":
        return "rgba(128, 128, 128, 0.1)";
      case "weekend":
        return "rgba(128, 128, 128, 0.06)";
      case "leave":
        return "rgba(33, 150, 243, 0.1)";
      case "pending":
        return "rgba(255, 152, 0, 0.1)";
      default:
        return "transparent";
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      selectedMonth.getMonth() === today.getMonth() &&
      selectedMonth.getFullYear() === today.getFullYear()
    );
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth);
    const firstDay = getFirstDayOfMonth(selectedMonth);
    const cells = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      cells.push(<Box key={`empty-${i}`} />);
    }

    // Day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dayInfo = attendanceData[dateKey];
      const status = dayInfo?.status;
      const totalHours = dayInfo?.total_hours;
      const today = isToday(day);
      const isSunday =
        new Date(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth(),
          day,
        ).getDay() === 0;
      const isSelected = selectedDate === dateKey;
      cells.push(
        <Box
          key={day}
          onClick={() => setSelectedDate(dateKey)}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 0.8,
            cursor: "pointer",
            position: "relative",
            borderRadius: 2,
            transition: "background-color 0.2s ease",
            outline: isSelected
              ? `2px solid ${theme.palette.primary.main}`
              : "none",
            backgroundColor: today
              ? theme.palette.primary.main
              : status && status !== "future"
                ? getStatusBgColor(status)
                : "transparent",
            "&:hover": {
              backgroundColor: today
                ? theme.palette.primary.dark
                : status && status !== "future"
                  ? getStatusBgColor(status)
                  : "rgba(0,0,0,0.04)",
            },
          }}
        >
          <Typography
            variant="body2"
            fontWeight={today ? 700 : 400}
            sx={{
              color: today ? "#fff" : isSunday ? "error.main" : "text.primary",
              fontSize: "0.85rem",
            }}
          >
            {day}
          </Typography>
          {totalHours && (
            <Typography
              variant="caption"
              sx={{
                color: today ? "rgba(255,255,255,0.85)" : "text.secondary",
                fontSize: "0.6rem",
                lineHeight: 1,
                mt: 0.2,
              }}
            >
              {totalHours}
            </Typography>
          )}
          {status && status !== "future" && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: today ? "#fff" : getStatusColor(status),
                mt: 0.3,
              }}
            />
          )}
        </Box>,
      );
    }
    return cells;
  };
  const fetchAttendanceData = async (preserveDate = false) => {
    if (!selectedEmployee) return;
    setCalendarLoading(true);
    if (!preserveDate) {
      setSelectedDate(pendingSelectDateRef.current);
      pendingSelectDateRef.current = null;
    }
    try {
      const month = selectedMonth.getMonth() + 1;
      const year = selectedMonth.getFullYear();
      const response = await fetchEmployeeAttendance(
        selectedEmployee,
        month,
        year,
      );
      const attendances = response.data.attendances;
      setRawAttendances(attendances);
      // Map attendance records by date with status and total_hours (summed)
      const mapped = {};
      attendances.forEach((record) => {
        const d = new Date(record.attendance_date);
        const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        const minutes = record.total_minutes || 0;
        if (!mapped[dateStr]) {
          mapped[dateStr] = {
            status: record.status || (record.time_out ? "present" : "pending"),
            totalMinutes: minutes,
            time_in: record.time_in,
            time_out: record.time_out,
          };
        } else {
          mapped[dateStr].totalMinutes += minutes;
          // Upgrade status if any record is present
          if (record.status === "present") mapped[dateStr].status = "present";
        }
      });
      // Format summed minutes into display string
      Object.keys(mapped).forEach((dateStr) => {
        const total = mapped[dateStr].totalMinutes;
        mapped[dateStr].total_hours =
          total > 0 ? `${Math.floor(total / 60)}h ${total % 60}m` : null;
      });
      // Mark past dates without records as absent (skip weekends & future)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysInMonth = new Date(year, month, 0).getDate();
      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const date = new Date(year, month - 1, day);
        if (date > today) continue; // skip future
        if (date.getDay() === 0) continue; // skip Sunday
        if (!mapped[dateStr]) {
          mapped[dateStr] = {
            status: "absent",
            totalMinutes: 0,
            total_hours: null,
          };
        }
      }
      setAttendanceData(mapped);
    } catch (error) {
      toast.error(error.message || "Failed to fetch attendance data");
    } finally {
      setCalendarLoading(false);
    }
  };

  const selectedMonthKey = `${selectedMonth.getFullYear()}-${selectedMonth.getMonth()}`;

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedEmployee, selectedMonthKey]);

  const fetchLocationData = async () => {
    if (!selectedEmployee || !selectedDate) {
      setLocationData([]);
      return;
    }
    setLocationsLoading(true);
    try {
      const response = await fetchEmployeeLocations(
        selectedEmployee,
        selectedDate,
      );
      setLocationData(response.data?.locations || []);
    } catch (error) {
      setLocationData([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  useEffect(() => {
    fetchLocationData();
  }, [selectedEmployee, selectedDate]);

  // Auto-scroll to the last location log when data loads
  useEffect(() => {
    if (locationData.length > 0) {
      const sorted = [...locationData]
        .filter((loc) => loc.latitude && loc.longitude)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      if (sorted.length > 0) {
        const lastLoc = sorted[sorted.length - 1];
        const lastId = lastLoc.id || sorted.length - 1;
        setHighlightedLocationId(lastId);
        // Small delay to let DOM render the log items
        setTimeout(() => {
          if (logRefs.current[lastId]) {
            logRefs.current[lastId].scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }, 200);
      }
    }
  }, [locationData]);

  // Listen for silent FCM location_ping notifications and refresh map
  useEffect(() => {
    const unsubscribe = onMessage(messaging, (payload) => {
      const event = payload.data?.event;
      const pingEmployeeId = payload.data?.employee_id;
      const pingDate = payload.data?.date;

      // Location ping — refresh the map
      if (event === "location_ping") {
        if (
          selectedEmployee &&
          String(selectedEmployee) === String(pingEmployeeId) &&
          selectedDate === pingDate
        ) {
          fetchLocationData();
        }
      }

      // Attendance update (check-in / check-out) — refresh calendar & logs
      if (event === "attendance_update") {
        if (
          selectedEmployee &&
          String(selectedEmployee) === String(pingEmployeeId)
        ) {
          fetchAttendanceData(true);
          // Also refresh location data if viewing that date
          if (selectedDate === pingDate) {
            fetchLocationData();
          }
        }
      }
    });
    return () => unsubscribe();
  }, [selectedEmployee, selectedDate]);

  const pathCoordinates = useMemo(() => {
    return locationData
      .filter((loc) => loc.latitude && loc.longitude)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map((loc) => [parseFloat(loc.latitude), parseFloat(loc.longitude)]);
  }, [locationData]);

  // Cluster nearby locations for map display
  const locationClusters = useMemo(() => {
    return clusterLocations(locationData, 50);
  }, [locationData]);

  const getLogsForDate = () => {
    if (!selectedDate) return [];
    return rawAttendances.filter((record) => {
      const d = new Date(record.attendance_date);
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      return dateStr === selectedDate;
    });
  };

  const formatTime = (datetime) => {
    if (!datetime) return "-";
    const date = new Date(datetime);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleEditClick = (record) => {
    setEditingAttendance(record);
    setEditDialogOpen(true);
  };

  const handleUpdateAttendance = async (id, updatedData) => {
    setUpdating(true);
    try {
      await updateAttendance(id, updatedData);
      toast.success("Attendance updated successfully");
      setEditDialogOpen(false);
      setEditingAttendance(null);
      // Refresh data
      fetchAttendanceData(true);
    } catch (error) {
      console.error(error);
      toast.error(
        error.message ||
          error.response?.data?.message ||
          "Failed to update attendance",
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleCreateAttendance = async (payload) => {
    setCreating(true);
    try {
      await createAttendance(payload);
      toast.success("Attendance added successfully");
      setCreateDialogOpen(false);

      // Show the new record — switch the view to the employee/month it belongs to
      const [year, month] = payload.attendance_date.split("-").map(Number);
      const employeeChanged =
        String(payload.employee_id) !== String(selectedEmployee);
      const monthChanged =
        month - 1 !== selectedMonth.getMonth() ||
        year !== selectedMonth.getFullYear();

      pendingSelectDateRef.current = payload.attendance_date;
      if (employeeChanged) {
        setSelectedEmployeeOption(null);
        setSelectedEmployee(payload.employee_id);
      }
      if (monthChanged) setSelectedMonth(new Date(year, month - 1, 1));
      // Neither changed, so the refetch effect won't fire — refresh in place
      if (!employeeChanged && !monthChanged) {
        pendingSelectDateRef.current = null;
        setSelectedDate(payload.attendance_date);
        fetchAttendanceData(true);
      }
    } catch (error) {
      console.error(error);
      toast.error(
        error.message ||
          error.response?.data?.message ||
          "Failed to add attendance",
      );
    } finally {
      setCreating(false);
    }
  };
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box component="h1" sx={{ m: 0 }}>
          Employee Attendance
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            borderRadius: theme.button.borderRadius,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          Add Attendance
        </Button>
      </Box>
      <Paper
        sx={{
          p: 2,
          borderRadius: theme.paper.borderRadius,
          backgroundColor: theme.paper.backgroundColor,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 4,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minWidth: 250,
              }}
            >
              <Typography
                variant="body2"
                color={theme.palette.primary.main}
                fontWeight={"bold"}
              >
                Select Employee
              </Typography>
              <AsyncAutocomplete
                fullWidth
                placeholder="Search employee..."
                value={selectedEmployee}
                selectedOption={selectedEmployeeOption}
                onChange={(id, option) => {
                  setSelectedEmployee(id);
                  setSelectedEmployeeOption(option);
                }}
                fetchPage={fetchEmployeePage}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                minWidth: 250,
              }}
            >
              <Typography
                variant="body2"
                color={theme.palette.primary.main}
                fontWeight={"bold"}
              >
                Select Month
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid",
                  borderColor: "rgba(0, 0, 0, 0.23)",
                  borderRadius: 1,
                  px: 1,
                  height: 40,
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    setSelectedMonth(
                      (prev) =>
                        new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
                    )
                  }
                >
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", userSelect: "none" }}
                >
                  {selectedMonth.toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    setSelectedMonth(
                      (prev) =>
                        new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
                    )
                  }
                >
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
          <Box>
            <Box sx={{ display: "flex", gap: 3, justifySelf: "flex-end" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "green",
                  }}
                />
                <Typography variant="body2">Present</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "red",
                  }}
                />
                <Typography variant="body2">Absent</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "orange",
                  }}
                />
                <Typography variant="body2">Half Day</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    backgroundColor: "grey",
                  }}
                />
                <Typography variant="body2">Holiday</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
      <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
        <Paper
          sx={{
            p: 3,
            borderRadius: theme.paper.borderRadius,
            backgroundColor: theme.paper.backgroundColor,
            width: "40%",
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color={theme.palette.primary.main}
            sx={{ mb: 2, textAlign: "center", letterSpacing: 1 }}
          >
            {selectedMonth.toLocaleString("default", {
              month: "long",
              year: "numeric",
            })}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: 0.5,
            }}
          >
            {dayNames.map((day) => (
              <Typography
                key={day}
                variant="caption"
                fontWeight="bold"
                sx={{
                  textAlign: "center",
                  py: 1,
                  color: day === "Sun" ? "error.main" : "text.secondary",
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                  letterSpacing: 0.5,
                }}
              >
                {day}
              </Typography>
            ))}
          </Box>
          <Box
            sx={{
              width: "100%",
              height: "1px",
              backgroundColor: "divider",
              mb: 1,
            }}
          />
          {calendarLoading ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0.5,
              }}
            >
              {Array.from({ length: 35 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rounded"
                  height={36}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0.5,
              }}
            >
              {renderCalendar()}
            </Box>
          )}
        </Paper>
        <Paper
          sx={{
            p: 2,
            borderRadius: theme.paper.borderRadius,
            backgroundColor: theme.paper.backgroundColor,
            width: "60%",
            maxHeight: 500,
            overflowY: "auto",
          }}
        >
          {selectedDate ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={theme.palette.primary.main}
                    sx={{ mb: 0.5 }}
                  >
                    Attendance Log
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(selectedDate)}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<HowToReg />}
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    borderRadius: theme.button.borderRadius,
                    whiteSpace: "nowrap",
                  }}
                >
                  Mark Present
                </Button>
              </Box>
              {getLogsForDate().length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
                        <TableCell sx={{ fontWeight: "bold" }}>Sr. No</TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Check In
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Check Out
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Worked
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          In Photo
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Out Photo
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          In Location
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Out Location
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold" }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getLogsForDate().map((record, index) => (
                        <TableRow key={record.id} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{formatTime(record.time_in)}</TableCell>
                          <TableCell>{formatTime(record.time_out)}</TableCell>
                          <TableCell>
                            {record.total_hours ||
                              (record.total_minutes
                                ? `${Math.floor(record.total_minutes / 60)}h ${record.total_minutes % 60}m`
                                : "-")}
                          </TableCell>
                          <TableCell>
                            {record.photo_in ? (
                              <Box
                                component="img"
                                src={`${BASE_URL}/${record.photo_in}`}
                                alt="Check In"
                                onClick={() =>
                                  setPreviewImage(
                                    `${BASE_URL}/${record.photo_in}`,
                                  )
                                }
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  objectFit: "cover",
                                  cursor: "pointer",
                                  "&:hover": { opacity: 0.8 },
                                }}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {record.photo_out ? (
                              <Box
                                component="img"
                                src={`${BASE_URL}/${record.photo_out}`}
                                alt="Check Out"
                                onClick={() =>
                                  setPreviewImage(
                                    `${BASE_URL}/${record.photo_out}`,
                                  )
                                }
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 1,
                                  objectFit: "cover",
                                  cursor: "pointer",
                                  "&:hover": { opacity: 0.8 },
                                }}
                              />
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {record.latitude_in && record.longitude_in ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  cursor: "pointer",
                                  "&:hover": { opacity: 0.7 },
                                }}
                                onClick={() =>
                                  window.open(
                                    `https://www.google.com/maps?q=${record.latitude_in},${record.longitude_in}`,
                                    "_blank",
                                  )
                                }
                              >
                                <LocationOn fontSize="small" color="primary" />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}
                                >
                                  {Number(record.latitude_in).toFixed(4)},<br />
                                  {Number(record.longitude_in).toFixed(4)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {record.latitude_out && record.longitude_out ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  cursor: "pointer",
                                  "&:hover": { opacity: 0.7 },
                                }}
                                onClick={() =>
                                  window.open(
                                    `https://www.google.com/maps?q=${record.latitude_out},${record.longitude_out}`,
                                    "_blank",
                                  )
                                }
                              >
                                <LocationOn fontSize="small" color="error" />
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: "0.65rem", lineHeight: 1.2 }}
                                >
                                  {Number(record.latitude_out).toFixed(4)},
                                  <br />
                                  {Number(record.longitude_out).toFixed(4)}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit Log" arrow placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(record)}
                                sx={{ color: theme.palette.primary.main }}
                                aria-label="Edit Log"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    py: 4,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No attendance records for this date
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<HowToReg />}
                    onClick={() => setCreateDialogOpen(true)}
                    sx={{
                      borderRadius: theme.button.borderRadius,
                      backgroundColor: theme.palette.primary.main,
                    }}
                  >
                    Mark Present
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                py: 4,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Select a date from the calendar to view logs
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
      {/* Location Map Card */}
      {selectedDate && selectedEmployee && (
        <Paper
          sx={{
            mt: 2,
            p: 2,
            borderRadius: theme.paper.borderRadius,
            backgroundColor: theme.paper.backgroundColor,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                color={theme.palette.primary.main}
              >
                Location Tracking
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(selectedDate)} &mdash; {locationData.length}{" "}
                location ping{locationData.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#4CAF50",
                  }}
                />
                <Typography variant="caption">First</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#1976d2",
                  }}
                />
                <Typography variant="caption">Intermediate</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: "#f44336",
                  }}
                />
                <Typography variant="caption">Last</Typography>
              </Box>
            </Box>
          </Box>
          {locationsLoading ? (
            <Box sx={{ display: "flex", gap: 2 }}>
              <Skeleton
                variant="rounded"
                height={450}
                sx={{ borderRadius: 2, flex: "0 0 65%" }}
              />
              <Box
                sx={{
                  flex: "0 0 35%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                {[...Array(6)].map((_, i) => (
                  <Skeleton
                    key={i}
                    variant="rounded"
                    height={60}
                    sx={{ borderRadius: 1.5 }}
                  />
                ))}
              </Box>
            </Box>
          ) : locationData.filter((loc) => loc.latitude && loc.longitude)
              .length > 0 ? (
            <Box sx={{ display: "flex", gap: 2 }}>
              {/* Map */}
              <Box sx={{ flex: "0 0 65%", minWidth: 0 }}>
                <MapContainer
                  key={`map-${selectedEmployee}-${selectedDate}-${locationData.length}`}
                  center={[
                    parseFloat(locationData[0].latitude),
                    parseFloat(locationData[0].longitude),
                  ]}
                  zoom={14}
                  style={{
                    height: "450px",
                    width: "100%",
                    borderRadius: 8,
                    border: "1px solid rgba(0,0,0,0.12)",
                  }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitBounds
                    locations={locationData.filter(
                      (loc) => loc.latitude && loc.longitude,
                    )}
                  />
                  <FlyToLocation location={flyToLocation} />
                  {pathCoordinates.length > 1 && (
                    <Polyline
                      positions={pathCoordinates}
                      pathOptions={{
                        color: "#1976d2",
                        weight: 3,
                        opacity: 0.6,
                        dashArray: "8, 8",
                      }}
                    />
                  )}
                  {locationClusters.map((cluster, clusterIndex) => {
                    const firstPing = cluster[0];
                    const lastPing = cluster[cluster.length - 1];
                    const arrivalTime = new Date(firstPing.created_at);
                    const leaveTime = new Date(lastPing.created_at);
                    const durationMs = leaveTime - arrivalTime;
                    const durationMins = Math.round(durationMs / 60000);
                    const durationStr =
                      durationMins >= 60
                        ? `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`
                        : `${durationMins}m`;
                    const isFirst = clusterIndex === 0;
                    const isLast = clusterIndex === locationClusters.length - 1;
                    const clusterId = firstPing.id || clusterIndex;
                    const isHighlighted = highlightedLocationId === clusterId;
                    const isBouncing = hoveredClusterId === clusterIndex;
                    return (
                      <Marker
                        key={`cluster-${clusterIndex}`}
                        position={[
                          parseFloat(firstPing.latitude),
                          parseFloat(firstPing.longitude),
                        ]}
                        icon={createNumberedIcon(
                          clusterIndex + 1,
                          locationClusters.length,
                          isHighlighted,
                          isBouncing,
                        )}
                        eventHandlers={{
                          click: () => {
                            setHighlightedLocationId(clusterId);
                            if (logRefs.current[clusterId]) {
                              logRefs.current[clusterId].scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            }
                          },
                        }}
                      >
                        <Popup>
                          <div style={{ minWidth: 180 }}>
                            <strong>
                              Location #{clusterIndex + 1}
                              {isFirst ? " (Start)" : isLast ? " (End)" : ""}
                            </strong>
                            <br />
                            <strong>Arrived:</strong>{" "}
                            {arrivalTime.toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                            <br />
                            <strong>Left:</strong>{" "}
                            {leaveTime.toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            })}
                            <br />
                            <strong>Duration:</strong> {durationStr}
                            <br />
                            <strong>Pings:</strong> {cluster.length}
                            <br />
                            <a
                              href={`https://www.google.com/maps?q=${firstPing.latitude},${firstPing.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ fontSize: 12 }}
                            >
                              Open in Google Maps
                            </a>
                          </div>
                        </Popup>
                      </Marker>
                    );
                  })}
                </MapContainer>
              </Box>
              {/* Location Logs Panel */}
              <Box
                sx={{
                  flex: "0 0 35%",
                  minWidth: 0,
                  maxHeight: 450,
                  overflowY: "auto",
                  pr: 0.5,
                  "&::-webkit-scrollbar": { width: 6 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(0,0,0,0.15)",
                    borderRadius: 3,
                  },
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  color="text.secondary"
                  sx={{
                    mb: 1,
                    display: "block",
                    position: "sticky",
                    top: 0,
                    backgroundColor: theme.paper.backgroundColor,
                    zIndex: 1,
                    py: 0.5,
                  }}
                >
                  Location Logs ({locationClusters.length} locations,{" "}
                  {locationData.length} pings)
                </Typography>
                {locationClusters.map((cluster, clusterIndex) => {
                  const firstPing = cluster[0];
                  const lastPing = cluster[cluster.length - 1];
                  const arrivalTime = new Date(firstPing.created_at);
                  const leaveTime = new Date(lastPing.created_at);
                  const durationMs = leaveTime - arrivalTime;
                  const durationMins = Math.round(durationMs / 60000);
                  const durationStr =
                    durationMins >= 60
                      ? `${Math.floor(durationMins / 60)}h ${durationMins % 60}m`
                      : `${durationMins}m`;
                  const isFirst = clusterIndex === 0;
                  const isLast = clusterIndex === locationClusters.length - 1;
                  const color = isFirst
                    ? "#4CAF50"
                    : isLast
                      ? "#f44336"
                      : "#1976d2";
                  const clusterId = firstPing.id || clusterIndex;
                  const isHighlighted = highlightedLocationId === clusterId;
                  return (
                    <Box
                      key={`cluster-log-${clusterIndex}`}
                      ref={(el) => {
                        logRefs.current[clusterId] = el;
                      }}
                      onClick={() => {
                        setHighlightedLocationId(clusterId);
                        setFlyToLocation({
                          lat: parseFloat(firstPing.latitude),
                          lng: parseFloat(firstPing.longitude),
                          timestamp: Date.now(),
                        });
                      }}
                      onMouseEnter={() => setHoveredClusterId(clusterIndex)}
                      onMouseLeave={() => setHoveredClusterId(null)}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        border: "1px solid",
                        borderColor: isHighlighted ? color : "divider",
                        backgroundColor: isHighlighted
                          ? `${color}12`
                          : "transparent",
                        boxShadow: isHighlighted
                          ? `0 0 0 1px ${color}40`
                          : "none",
                        "&:hover": {
                          borderColor: color,
                          backgroundColor: `${color}08`,
                        },
                      }}
                    >
                      {/* Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            minWidth: 28,
                            borderRadius: "50%",
                            backgroundColor: color,
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: "bold",
                          }}
                        >
                          {clusterIndex + 1}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight="600"
                            sx={{ fontSize: 13 }}
                          >
                            Location #{clusterIndex + 1}
                            {isFirst ? " (Start)" : isLast ? " (End)" : ""}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${cluster.length} ping${cluster.length > 1 ? "s" : ""}`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 10,
                            fontWeight: 600,
                            backgroundColor: `${color}18`,
                            color: color,
                          }}
                        />
                      </Box>
                      {/* Time Info */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          flexWrap: "wrap",
                          ml: 5.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          <strong>Arrived:</strong>{" "}
                          {arrivalTime.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Left:</strong>{" "}
                          {leaveTime.toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          <strong>Duration:</strong> {durationStr}
                        </Typography>
                      </Box>
                      {/* Location */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          mt: 0.5,
                          ml: 5.5,
                        }}
                      >
                        <LocationOn
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {Number(firstPing.latitude).toFixed(5)},{" "}
                          {Number(firstPing.longitude).toFixed(5)}
                        </Typography>
                        <a
                          href={`https://www.google.com/maps?q=${firstPing.latitude},${firstPing.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: 11,
                            color: "#1976d2",
                            textDecoration: "none",
                            marginLeft: 8,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          Open ↗
                        </a>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No location data available for this date
              </Typography>
            </Box>
          )}
        </Paper>
      )}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2, overflow: "hidden", position: "relative" },
        }}
      >
        <IconButton
          onClick={() => setPreviewImage(null)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: "rgba(0,0,0,0.5)",
            color: "#fff",
            "&:hover": { backgroundColor: "rgba(0,0,0,0.7)" },
          }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
        {previewImage && (
          <Box
            component="img"
            src={previewImage}
            alt="Preview"
            sx={{ width: "100%", maxHeight: "80vh", objectFit: "contain" }}
          />
        )}
      </Dialog>

      {/* Create Dialog */}
      <CreateAttendanceDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreateAttendance}
        loading={creating}
        employeeId={selectedEmployee}
        employeeOption={selectedEmployeeOption}
        date={selectedDate}
      />

      {/* Edit Dialog */}
      {editingAttendance && (
        <EditAttendanceDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          attendance={editingAttendance}
          onUpdate={handleUpdateAttendance}
          loading={updating}
        />
      )}
    </Box>
  );
};
export default AttendancePage;
